<template>
  <!-- Error Message -->
  <PageError v-if="error"
   :error="error"
   :errorCode="errorCode"
  />
  <div
    v-if="error"
    class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
    role="alert"
    >
    <strong class="font-bold">Error! </strong>
    <span class="block sm:inline">{{ error }}</span>
  </div>
  <div v-else-if="canViewFortigate || user?.is_staff" class="p-6">
    <!-- Header Section -->
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
        Fortigate - {{ filteredFortigate.length }}
        <span v-if="isFiltered" class="text-sm text-gray-500">(filtered)</span>
      </h1>
      <div class="flex gap-4">
        <div class="relative group">
        <button
          type="button"
          class="hover:bg-transparent bg-transparent"
          @click="startTutorial"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
        </button>
          <span class="absolute top-full text-xs right-1/2 -translate-y-1/2 glass-card mb-1 px-2 py-0.5 glass-card opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">Tutorial</span>
        </div>
        <button v-if="canAddFortigate || user?.is_staff"
          id="add-fortigate-btn"
          @click="goToAddFortigate"
          class="btn bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition duration-200"
        >
          Add Fortigate
        </button>
      </div>
    </div>

    <!-- Organization Tabs -->
    <OrganizationTabs
      v-if="user?.is_staff || isServiceProviderUser"
      :organizations="tenant.organizations.value"
      :selected-org-id="tenant.currentOrganizationId.value"
      :loading="tenant.isLoading.value"
      :show-tabs="true"
      loading-text="Loading Fortigates..."
      @select="handleOrgSelect"
    />

    <!-- Search and Filter Section -->
    <div
      class="flex flex-col md:flex-row md:items-center md:justify-between mb-6"
    >
      <!-- Search Bar -->
      <div class="mb-4 md:mb-0 md:flex-1 md:mr-4">
        <SearchBar
          v-model="searchQuery"
          placeholderText="Search fortigates by job name..."
        />
      </div>

      <!-- Bulk Action Section -->
      <div class="flex items-center mt-4 md:mt-0 space-x-2">
        <GlassSelect
          v-model="bulkAction"
          :options="canDeleteFortigate || user?.is_staff ? [{ value: 'delete', label: 'Delete selected items' }] : []"
          placeholder="Bulk Actions"
          :valueKey="'value'"
          :labelKey="'label'"
        />
        <button
          @click="applyBulkAction"
          :disabled="!bulkAction || selectedFortigates.length === 0"
          :class="[
            'px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200',
            bulkAction && selectedFortigates.length
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed',
          ]"
        >
          Apply
        </button>
      </div>
    </div>

    <!-- fortigates Table -->
    <GenericTable
      id="fortigates-table"
      ref="table"
      :columns="columns"
      :items="paginatedFortigates"
      itemKey="id"
      :selectable="true"
      @update:selectedItems="updateSelectedFortigates"
      @rowClick="goToFortigateDetail"
    >
      <!-- Custom cell templates -->
      <template #created_date="{ value }">
        {{ formatDateTime(value) }}
      </template>
    </GenericTable>

    <!-- Pagination -->
    <div
      class="flex flex-col md:flex-row items-center justify-between mt-8 space-y-4 md:space-y-0"
    >
      <div class="flex items-center space-x-2">
        <button
          @click="changePage(-1)"
          :disabled="currentPage === 1"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 dark:text-white hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
        >
          Previous
        </button>
        <button
          @click="changePage(1)"
          :disabled="currentPage === totalPages"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 dark:text-white hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
        >
          Next
        </button>
      </div>
      <div class="text-gray-700 dark:text-white">
        Page {{ currentPage }} of {{ totalPages }}
      </div>
      <div>
        <GlassSelect
          v-model="itemsPerPage"
          :options="[{ value: 10, label: '10 per page' }, { value: 20, label: '20 per page' }, { value: 50, label: '50 per page' }, { value: 100, label: '100 per page' }, { value: -1, label: 'All' }]"
          :valueKey="'value'"
          :labelKey="'label'"
          placeholder="Items per page"
          @change="updatePagination"
        />
      </div>
    </div>


    <DeleteModal
      v-if="deletingItem"
      :deleting-item="deletingItem"
      :componet-objects="selectedObjects"
      :is-deleting="isDeleting"
      @delete="confirmDelete"
      @close="deletingItem=false"
      :isChildObject="true"
      :count="count"
    />
  </div>
</template>

<script>
import { getAllFortigates, deleteFortigate } from "@/services/fortigateService";
import { getAllOrgs } from "@/services/orgService";
import { getSites } from "@/services/siteService";
import SearchBar from "@/components/SearchBar.vue";
import GenericTable from "@/components/GenericTable.vue";
import { useStore } from "@/store";
import { useMultiTenant } from "@/composables/useMultiTenant";
import OrganizationTabs from "@/components/OrganizationTabs.vue";
import PageError from "@/components/PageError.vue";
import GlassSelect from "@/components/GlassSelect.vue";
import DeleteModal from "@/components/DeleteModal.vue";
import { useTutorial } from "@/composables/tutorial";

export default {
  name: "FortigateView",
  components: {
    SearchBar,
    GenericTable,
    OrganizationTabs,
    PageError,
    GlassSelect,
    DeleteModal
  },
  data() {
    return {
      fortigates: [],
      searchQuery: "",
      filteredFortigate: [],
      currentPage: 1,
      itemsPerPage: 10,
      selectedFortigates: [],
      deletingItem: false,
      bulkAction: "",
      columns: [
        { key: "job_name", label: "Job Name", sortable: true, width: "25%" },
        { key: "site_name", label: "Site", sortable: true, width: "20%" },
        {
          key: "org_name",
          label: "Organisation",
          sortable: true,
          width: "25%",
        },
        {
          key: "created_date",
          label: "Created Date",
          sortable: true,
          width: "10%",
        },
      ],
      canAddFortigate: true,
      canDeleteFortigate: true,
      canViewFortigate: true,
      error: "",
      isDeleting: false,
      fortigateTutorialSteps: [
        {
          element: '#searchbar',
          action: 'display',
          message: 'Search fortigates by job name to filter the list.'
        },
        {
          element: '#add-fortigate-btn',
          action: 'display',
          message: 'Click here to add a new Fortigate.'
        },
        {
          element: '#fortigates-table',
          action: 'display',
          message: 'Click any row to view or edit Fortigate details.'
        },
      ],
    };
  },
  setup() {
    const tenant = useMultiTenant();
    const { start } = useTutorial();
    return { tenant, _start: start };
  },
  computed: {
    user(){
      return useStore.getUser();
    },
    isServiceProviderUser(){
      return useStore.isServiceProviderUser();
    },
    isFiltered() {
      return this.searchQuery !== "";
    },
    totalPages() {
      return this.itemsPerPage === -1
        ? 1
        : Math.ceil(this.filteredFortigate.length / this.itemsPerPage);
    },
    paginatedFortigates() {
      if (this.itemsPerPage === -1) {
        return this.filteredFortigate;
      }
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredFortigate.slice(start, end);
    },
    selectedObjects(){
        return this.selectedFortigates.map(fortigate => ({
          name: "Fortigate",
          value: fortigate.job_name,
       }));
    },
    count(){
      return [{name: this.selectedObjects.length === 1 ? 'Fortigate' : "Fortigates", value: this.selectedObjects.length}]
    }
  },
  watch: {
    searchQuery() {
      this.applyFilters();
    },
    user: {
      handler(user) {
        if (user) {
          this.checkPermissions();
        }
      },
      immediate: true
    },
  },
  async mounted() {
    this.checkPermissions();
    await this.tenant.fetchOrganizations();
    if (this.tenant.currentOrganizationId.value) {
      await this.tenant.syncUrlParam();
      await this.fetchFortigates(this.tenant.currentSchemaName.value);
    } else {
      await this.tenant.loadInitialData(this.fetchFortigates);
    }
  },
  methods: {
    async startTutorial() {
      await this._start(this.fortigateTutorialSteps);
    },
    goToAddFortigate() {
      this.$router.push({
        name: "AddFortigate",
        query: this.tenant.addSchemaParam()
       });
    },
    async checkPermissions(){
      if (this.user) {
        this.canAddFortigate = await useStore.hasPermission("Can add fortigate");
        this.canDeleteFortigate= await useStore.hasPermission("Can delete fortigate");
        this.canViewFortigate = await useStore.hasPermission("Can view fortigate");
      }
    },
    async handleOrgSelect(orgId) {
      try {
        this.error = "";
        await this.tenant.switchOrganization(orgId, this.fetchFortigates);
      } catch (error) {
        this.error = "Failed to switch organization";
        console.error('Organization select error:', error);
      }
    },
    async fetchFortigates(schemaName=null) {
      try {
        if(!schemaName){
          const validation = this.tenant.validateSchemaForOperation();
          if (!validation.valid) {
            this.error = "Malformed request. Failed to load fortigates!";
            this.errorCode = "422!";
            return;
          }
          schemaName = validation.schemaName;
        }
        const fortigates = await getAllFortigates(schemaName);
        const orgs = await getAllOrgs();
        const sites = await getSites(schemaName);

        this.fortigates = [...fortigates].sort((a, b) => new Date(b.last_modified) - new Date(a.last_modified));

        this.fortigates = this.fortigates.map((fortigate) => ({
            ...fortigate,
            org_name: orgs.find(org => org.id === fortigate.org).name,
            site_name: sites.find(site => site.id === fortigate.site).name
        }))

        this.applyFilters();
      } catch (error) {
        console.error("Error fetching fortigates:", error);
        if (error.response) {
          switch (error.response.status) {
            case 404:
              this.error = error.response.status +" The probe resource was not found. Please check the API endpoint."
              break;
            case 401:
              this.error = error.response.status + " Unauthorized access. Please check your authentication token or log in again."
              break;
            case 403:
              this.error = error.response.status + " You don't have permission to access this resource."
                          + " Contact a staff member to assign you the permissions to access this page. ";
              break;
            default:
              this.error = error.response.status =   error.response.data.detail || "Unknown error"
          }
        }else if (error.request) {
          alert(
            "No response received from the server. Please check your internet connection."
          );
        } else {
          alert(`An error occurred: ${error.message}`);
        }
      }
    },
    goToFortigateDetail(fortigate) {
      this.$router.push({
        name: "FortigateDetails",
        params: { id: fortigate.id },
        query: this.tenant.addSchemaParam(),
      });
    },
    updateSelectedFortigates(selectedItems) {
      this.selectedFortigates = selectedItems;
    },
    async applyBulkAction() {
      if (this.bulkAction === "delete" ) {
        this.deletingItem = true;
      }
      this.bulkAction = "";
    },
    async confirmDelete(schemaName=null) {
      this.isDeleting = true;
      if (!schemaName) {
        const validation = this.tenant.validateSchemaForOperation();
        if (!validation.valid) {
          this.error = "Malformed request. Failed to delete selected Fortigates!";
          this.errorCode = "422!";
          return;
        }
        schemaName = validation.schemaName;
      }
      await this.deleteSelectedFortigates(schemaName);
      this.deletingItem = false; 
      this.bulkAction = "";
    },
    async deleteSelectedFortigates() {
      if (this.selectedFortigates.length === 0) {
        alert("No fortigates selected for deletion");
        return;
      }
      const schemaName = this.tenant.currentSchemaName.value; 
      if (!schemaName) {
        this.error = "Malformed request. Failed to delete selected Fortigates!";
        this.errorCode = "422!";
        return;
      }

      try {
        const deletePromises = this.selectedFortigates.map((fortigate) =>
          deleteFortigate(fortigate.id, schemaName)
        );
        await Promise.all(deletePromises);
        
        this.fetchFortigates(schemaName)
        this.$refs.table.clearSelection();
        this.applyFilters();
        this.selectedFortigates = [];
 
      } catch (error) {
        console.error("Error during bulk delete:", error);
        alert("An error occurred while deleting fortigates. Please try again.");
      }  finally {
          this.isDeleting = false;
      }
    },
    formatDateTime(dateString) {
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZoneName: "short",
      };
      return new Date(dateString).toLocaleString(undefined, options);
    },
    async deleteFortigateItem(id, schemaName=null) {
      if (confirm("Are you sure you want to delete this fortigate?")) {
        try {
          await deleteFortigate(id, schemaName);
          this.fetchFortigates(schemaName)
          this.applyFilters(); // Re-apply filters to update the view
        } catch (error) {
          console.error("Error deleting fortigate:", error);
          // Handle error (e.g., show error message to user)
        }
      }
    },
    applyFilters() {
      const query = (this.searchQuery || '').toLowerCase();
      this.filteredFortigate = (this.fortigates || []).filter((fortigate) => {
        const jobName = (fortigate?.job_name || '').toLowerCase();
        return jobName.includes(query);
      });
      this.currentPage = 1;
    },
    changePage(delta) {
      this.currentPage += delta;
    },
    updatePagination() {
      this.currentPage = 1;
    },
    updateSelectedFortigates(selectedItems) {
      this.selectedFortigates = selectedItems.filter((item => !item.marked_for_deletion));
    },
    formatDateTime(dateString) {
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZoneName: "short",
      };
      return new Date(dateString).toLocaleString(undefined, options);
    },
  },
};
</script>